import { Chip } from "@mui/material";
import { ChipWrap } from "components/ChipWrap";
import { Box } from "@mui/system";
import { useNotification } from "providers/Notification";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { AddNewApplication } from "./AddApplication";
import { FormDrawer } from "components/FormDrawer";
import { ListPageHeader } from "components/ListPageHeader";
import { ConfirmDialog } from "components/ConfirmDialog";
import DataTable, { Column } from "components/Table";
import { useResourceList } from "hooks/useResourceList";
import { useDeleteDialog } from "hooks/useDeleteDialog";
import { useFormDrawer } from "hooks/useFormDrawer";
import { formatDateTime, fromUnix } from "helpers/dateFormat";
import { useRole } from "hooks/useRole";

interface RuleState {
    id: string
    name: string
}

interface AppsListStateItem {
    id: string
    name: string
    createdAt: Date
    updatedAt: Date
    rules?: RuleState[]
    readOnly?: boolean
}

type columnsType = "name" | "usedBy" | "createdat" | "updatedat";

const defaultColumns: Column<columnsType>[] = [
    { columnId: "name", width: "40%", direction: "desc", sortOrder: 1 },
    { columnId: "usedBy", width: "20%" },
    { columnId: "createdat", width: "20%", direction: "desc", sortOrder: 2 },
    { columnId: "updatedat", width: "20%" },
];

function toApp(x: any): AppsListStateItem {
    return {
        id: x.id,
        name: x.name,
        createdAt: fromUnix(x.createdAt),
        updatedAt: fromUnix(x.updatedAt),
        rules: x.rules && x.rules.map((item: any) => ({
            id: item.id,
            name: item.name,
        })),
        readOnly: x.readOnly,
    };
}

export default function Apps() {
    const notification = useNotification();
    const { canEditPolicy } = useRole();

    const list = useResourceList<AppsListStateItem, columnsType>({
        url: "/api/apps",
        columnsKey: "dataGrid_ApplicationTable_columns",
        defaultColumns,
        mapItem: toApp,
        paginated: true,
        sorted: true,
        searchable: true,
    });

    const drawer = useFormDrawer();
    const del = useDeleteDialog({
        url: "/api/apps",
        label: "application",
        onDeleted: list.reload,
    });

    return <>
        <Box display="flex" flexDirection="column">
            <ListPageHeader
                title="Applications"
                search={list.search}
                onSearchChange={list.setSearch}
                searchAllFields={list.searchAllFields}
                onSearchAllFieldsChange={list.setSearchAllFields}
                actionId="new-application-button"
                actionLabel="Add New Application"
                // NS-753
                onAction={canEditPolicy ? drawer.add : undefined}
            />

            <DataTable
                {...list.tableProps}
                columns={{
                    name: {
                        displayName: "Name",
                        sortable: true,
                        render: (x) => x.name,
                    },
                    usedBy: {
                        displayName: "Used By Rule(s)",
                        render: (x) => {
                            return <ChipWrap>
                                {(x.rules && x.rules.map(y => <Chip label={y.name.trim()} variant="outlined" size="small" color="primary" />))}
                            </ChipWrap>;
                        }
                    },
                    createdat: {
                        displayName: "Created At",
                        sortable: true,
                        render: (x) => {
                            return formatDateTime(x.createdAt);
                        }
                    },
                    updatedat: {
                        displayName: "Updated At",
                        sortable: true,
                        render: (x) => {
                            return formatDateTime(x.updatedAt);
                        }
                    },
                }}
                rowOptionsMenu={[
                    {
                        icon: <EditIcon className="edit-button" fontSize="small" />,
                        text: canEditPolicy ? "Edit" : "View",
                        onClick: (x) => {
                            drawer.edit(x.id);
                        }
                    },
                    {
                        icon: <DeleteIcon className="delete-button" fontSize="small" />,
                        text: "Delete",
                        onClick: (x) => {
                            if (x.rules && x.rules.length > 0) {
                                return notification.Display({
                                    type: "error",
                                    title: "Validation Error",
                                    message: `Cannot delete ${x.name} because it is in use by ${x.rules.length === 1 ? "a Rule" : "some Rules"} (${x.rules.map(i => i.name).join(", ")})`,
                                });
                            }

                            del.ask(x.name, x.id);
                        },
                        isVisible: (x) => !x.readOnly && canEditPolicy
                    },
                ]}
            />
        </Box>

        <ConfirmDialog {...del.dialogProps} title="Delete Application" />

        <FormDrawer {...drawer.drawerProps}>
            <AddNewApplication
                appId={drawer.editingId}
                onExit={drawer.close}
                onSave={() => { list.reload(); drawer.close(); }}
            />
        </FormDrawer>
    </>;
}


